/**
 * Pure helpers for exercise library page routing and patient display.
 */

/** Survives full route changes so returning to /exercise-library can reopen edit when the URL drops ?prescription_id. */
export const EXERCISE_LIBRARY_EDIT_PRESCRIPTION_SESSION_KEY =
  'exercise-library-edit-prescription-id';

function coercePrescriptionId(rawValue) {
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return undefined;
  }
  if (typeof rawValue === 'string' && rawValue.length > 0) {
    return rawValue;
  }
  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return rawValue;
  }
  return undefined;
}

export function persistExerciseLibraryEditPrescriptionId(prescriptionId) {
  try {
    const normalizedPrescriptionId = coercePrescriptionId(prescriptionId);
    if (normalizedPrescriptionId === undefined) {
      return;
    }
    sessionStorage.setItem(
      EXERCISE_LIBRARY_EDIT_PRESCRIPTION_SESSION_KEY,
      String(normalizedPrescriptionId)
    );
  } catch {
    // ignore
  }
}

export function clearExerciseLibraryEditPrescriptionId() {
  try {
    sessionStorage.removeItem(EXERCISE_LIBRARY_EDIT_PRESCRIPTION_SESSION_KEY);
  } catch {
    // ignore
  }
}

export function readExerciseLibraryEditPrescriptionIdFromSession() {
  try {
    const raw = sessionStorage.getItem(EXERCISE_LIBRARY_EDIT_PRESCRIPTION_SESSION_KEY);
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const normalizedPrescriptionId = coercePrescriptionId(raw);
    return normalizedPrescriptionId === undefined ? null : normalizedPrescriptionId;
  } catch {
    return null;
  }
}

export function resolvePrescriptionId(locationState, prescriptionIdSearchString) {
  const fromState =
    locationState !== undefined &&
    locationState !== null &&
    typeof locationState === 'object' &&
    'prescriptionId' in locationState
      ? locationState.prescriptionId
      : undefined;
  const fromSearch = coercePrescriptionId(prescriptionIdSearchString);
  const raw = fromState ?? fromSearch;
  const normalizedPrescriptionId = coercePrescriptionId(raw);
  if (normalizedPrescriptionId === undefined) {
    return undefined;
  }
  return normalizedPrescriptionId;
}

export function shouldNavigateToNotFoundForPrescriptionError(error) {
  const status = error?.response?.status;
  if (status === 403 || status === 404) {
    return true;
  }

  const rawMessage = error?.response?.data?.message ?? error?.message;
  if (!rawMessage) {
    return false;
  }

  const message = (
    Array.isArray(rawMessage) ? rawMessage.join(' ') : String(rawMessage)
  ).toLowerCase();
  return (
    message.includes('permission') || message.includes('forbidden') || message.includes('not found')
  );
}

export function getPatientFromLocationState(locationState) {
  if (locationState === undefined || locationState === null || typeof locationState !== 'object') {
    return undefined;
  }
  return locationState.patient;
}

/** Marks that a deep-assign flow is active so the patient can be restored without ?assign=1 in the URL. */
export const EXERCISE_LIBRARY_ASSIGN_FLOW_ACTIVE_KEY = 'exercise-library-assign-flow-active';

export function persistAssignFlowActive() {
  try {
    sessionStorage.setItem(EXERCISE_LIBRARY_ASSIGN_FLOW_ACTIVE_KEY, '1');
  } catch {
    // ignore
  }
}

export function isAssignFlowActive() {
  try {
    return sessionStorage.getItem(EXERCISE_LIBRARY_ASSIGN_FLOW_ACTIVE_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearAssignFlowActive() {
  try {
    sessionStorage.removeItem(EXERCISE_LIBRARY_ASSIGN_FLOW_ACTIVE_KEY);
  } catch {
    // ignore
  }
}

/** Persists the last-known builderRouteKey so the layout effect can skip clearing on remount. */
export const EXERCISE_LIBRARY_BUILDER_ROUTE_KEY = 'exercise-library-builder-route-key';

export function persistBuilderRouteKey(key) {
  try {
    if (key) {
      sessionStorage.setItem(EXERCISE_LIBRARY_BUILDER_ROUTE_KEY, key);
    } else {
      sessionStorage.removeItem(EXERCISE_LIBRARY_BUILDER_ROUTE_KEY);
    }
  } catch {
    // ignore
  }
}

export function readBuilderRouteKeyFromSession() {
  try {
    return sessionStorage.getItem(EXERCISE_LIBRARY_BUILDER_ROUTE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export function clearBuilderRouteKey() {
  try {
    sessionStorage.removeItem(EXERCISE_LIBRARY_BUILDER_ROUTE_KEY);
  } catch {
    // ignore
  }
}

/** Session backup when mobile/WebView drops `location.state` after patient → library navigation. */
export const EXERCISE_LIBRARY_ASSIGN_PATIENT_SESSION_KEY = 'exercise-library-assign-patient-v1';

export function persistExerciseLibraryAssignPatient(patient) {
  if (!patient || typeof patient !== 'object') {
    return;
  }
  const normalized = normalizeNavigationPatient(patient);
  if (!normalized) {
    return;
  }
  try {
    sessionStorage.setItem(EXERCISE_LIBRARY_ASSIGN_PATIENT_SESSION_KEY, JSON.stringify(normalized));
  } catch {
    // ignore
  }
}

export function readExerciseLibraryAssignPatientFromSession() {
  try {
    const raw = sessionStorage.getItem(EXERCISE_LIBRARY_ASSIGN_PATIENT_SESSION_KEY);
    if (!raw) {
      return undefined;
    }
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object') {
      return undefined;
    }
    return normalizeNavigationPatient(p) ? p : undefined;
  } catch {
    return undefined;
  }
}

export function clearExerciseLibraryAssignPatientSession() {
  try {
    sessionStorage.removeItem(EXERCISE_LIBRARY_ASSIGN_PATIENT_SESSION_KEY);
  } catch {
    // ignore
  }
}

/** True when user entered library from a patient “assign prescription” entry (query, state flag, or session). */
export function isAssignPatientFlowEntry(locationState, searchString) {
  const q = searchString && typeof searchString === 'string' ? searchString : '';
  const params = new URLSearchParams(q.startsWith('?') ? q.slice(1) : q);
  if (params.get('assign') === '1') {
    return true;
  }
  return Boolean(
    locationState && typeof locationState === 'object' && locationState.assignFromPatient === true
  );
}

/**
 * Resolves `patient` for the exercise library: nested `state.patient`, flat keys, or session (assign flow only).
 */
export function resolveExerciseLibraryPatientFromNavigation(locationState, searchString) {
  const allowSession = isAssignPatientFlowEntry(locationState, searchString);

  if (
    locationState &&
    typeof locationState === 'object' &&
    locationState.patient &&
    typeof locationState.patient === 'object'
  ) {
    return locationState.patient;
  }

  if (locationState && typeof locationState === 'object') {
    const rawId = locationState.patientId ?? locationState.patientID ?? locationState.id;
    const rawLabel =
      locationState.patientName ??
      locationState.label ??
      locationState.name ??
      locationState.displayName;
    const hasId = rawId !== undefined && rawId !== null && rawId !== '';
    const hasLabel = typeof rawLabel === 'string' && rawLabel.trim().length > 0;
    if (hasId || hasLabel) {
      const patient = {};
      if (hasLabel) patient.label = rawLabel.trim();
      if (hasId) patient.value = rawId;
      if (Object.keys(patient).length) return patient;
    }
  }

  if (allowSession) {
    const cached = readExerciseLibraryAssignPatientFromSession();
    if (cached) {
      return cached;
    }
  }

  return undefined;
}

export function stripAssignPatientQueryParam(searchString) {
  const raw = searchString && typeof searchString === 'string' ? searchString : '';
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  params.delete('assign');
  const next = params.toString();
  return next ? `?${next}` : '';
}

/** Where to send the user back to once a deep-assign (from a patient's page) prescription is created. */
export const EXERCISE_LIBRARY_ASSIGN_RETURN_PATH_KEY = 'exercise-library-assign-return-path';

export function persistExerciseLibraryAssignReturnPath(path) {
  try {
    if (path) {
      sessionStorage.setItem(EXERCISE_LIBRARY_ASSIGN_RETURN_PATH_KEY, path);
    }
  } catch {
    // ignore
  }
}

export function readExerciseLibraryAssignReturnPath() {
  try {
    return sessionStorage.getItem(EXERCISE_LIBRARY_ASSIGN_RETURN_PATH_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export function clearExerciseLibraryAssignReturnPath() {
  try {
    sessionStorage.removeItem(EXERCISE_LIBRARY_ASSIGN_RETURN_PATH_KEY);
  } catch {
    // ignore
  }
}

/** Call from patient profile / lists when opening the prescription builder for a specific patient. */
export function navigateToExerciseLibraryForPatientAssign(navigate, { label, value: rawValue }) {
  const id = rawValue !== undefined && rawValue !== null && rawValue !== '' ? rawValue : undefined;
  const trimmedLabel = (label ?? '').trim();
  if (!trimmedLabel && id === undefined) {
    navigate('/exercise-library');
    return;
  }
  const patient = {
    ...(trimmedLabel ? { label: trimmedLabel } : {}),
    ...(id !== undefined ? { value: id } : {}),
  };
  persistExerciseLibraryAssignPatient(patient);
  persistAssignFlowActive();
  try {
    persistExerciseLibraryAssignReturnPath(`${window.location.pathname}${window.location.search}`);
  } catch {
    // ignore
  }
  navigate('/exercise-library?assign=1&flow=select-exercises&step=add', {
    state: { assignFromPatient: true, patient },
  });
}

/** Skip persisting to localStorage while editing an existing prescription. */
export function shouldSkipPrescriptionDraftPersist(prescription_id) {
  return prescription_id !== undefined && prescription_id !== null;
}

/**
 * Skip hydrating from localStorage when API/navigation owns initial state (edit or deep assign entry).
 * Persist (autosave / unmount) still runs for new prescriptions so leaving the page keeps a snapshot.
 */
export function shouldBlockPrescriptionDraftRestore(prescription_id) {
  return prescription_id !== undefined && prescription_id !== null;
}

function navigationPatientId(patientFromState) {
  if (!patientFromState || typeof patientFromState !== 'object') {
    return null;
  }
  const raw =
    patientFromState.value ?? patientFromState.id ?? patientFromState.patientId ?? undefined;
  if (raw === undefined || raw === null || raw === '') {
    return null;
  }
  return raw;
}

export function normalizeNavigationPatient(patientFromState) {
  const value = navigationPatientId(patientFromState);
  if (value === null) {
    return null;
  }
  let label = '';
  if (typeof patientFromState.label === 'string') {
    label = patientFromState.label.trim();
  } else if (typeof patientFromState.name === 'string') {
    label = patientFromState.name.trim();
  }
  return { label, value };
}

export function resolveAssignPatientFromNavigationAndForm(patientFromState, formPatientOption) {
  const merged = {};
  if (
    patientFromState &&
    typeof patientFromState === 'object' &&
    !Array.isArray(patientFromState)
  ) {
    Object.assign(merged, patientFromState);
  }
  if (
    formPatientOption &&
    typeof formPatientOption === 'object' &&
    !Array.isArray(formPatientOption)
  ) {
    Object.assign(merged, formPatientOption);
  }
  return Object.keys(merged).length > 0 ? normalizeNavigationPatient(merged) : null;
}

export function patientLabelFromFormOption(watchedPatients) {
  if (
    !watchedPatients ||
    typeof watchedPatients !== 'object' ||
    Array.isArray(watchedPatients) ||
    typeof watchedPatients.label !== 'string' ||
    watchedPatients.label.trim().length === 0
  ) {
    return undefined;
  }
  return watchedPatients.label.trim();
}

export function patientLabelFromPatientState(patientFromState) {
  if (!patientFromState || typeof patientFromState !== 'object') {
    return undefined;
  }
  if (typeof patientFromState.label === 'string' && patientFromState.label.trim().length > 0) {
    return patientFromState.label.trim();
  }
  if (typeof patientFromState.name === 'string' && patientFromState.name.trim().length > 0) {
    return patientFromState.name.trim();
  }
  return undefined;
}

/** Stable JSON signature of prescription cards for ETA comparison (edit mode). */
export function makeEtaSignature(cards) {
  if (!Array.isArray(cards) || cards.length === 0) {
    return '';
  }
  return JSON.stringify(
    cards.map(c => ({
      id: c.id,
      basedOnRep: c.basedOnRep,
      basedOnTime: c.basedOnTime,
      basedOnSingleRep: c.basedOnSingleRep,
      sets: c.sets,
      reps: c.reps,
    }))
  );
}

export function buildPatientNameForHeader({
  patientFromApi,
  labelFromForm,
  labelFromNavigation,
  suppressNavigationLabel,
}) {
  const patientNameFromApi =
    patientFromApi && typeof patientFromApi === 'object'
      ? patientFromApi.fullName ||
        [patientFromApi.firstName, patientFromApi.lastName].filter(Boolean).join(' ')
      : undefined;
  return (
    patientNameFromApi ??
    labelFromForm ??
    (!suppressNavigationLabel ? labelFromNavigation : undefined)
  );
}
